import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { enviarMail } from './apiEmail';
import { auth, firestore } from "./firebase";
import "./Login.css";

function Login({ setUsuario }) {
  const [modoRegistro, setModoRegistro] = useState(false);
  const [nombre, setNombre] = useState("");
  const [apellido, setApellido] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [celular, setCelular] = useState("");
  const [congregacion, setCongregacion] = useState("");
  const navigate = useNavigate();

  const errorMessages = {
    "auth/invalid-credential": "Las credenciales proporcionadas son incorrectas o han expirado.",
    "auth/user-not-found": "No se encontró ningún usuario con ese correo.",
    "auth/wrong-password": "La contraseña es incorrecta.",
    "auth/email-already-in-use": "El correo ya está en uso.",
    "auth/weak-password": "La contraseña es demasiado débil.",
  };

  // URLs directas (tu dominio)
  const URL_RESERVAR = "https://reservascuchi.com/reservas";
  const URL_INFO     = "https://reservascuchi.com/info";

  // Contacto (WhatsApp)
  const CONTACTO_TEL = "098 925 827";
  const CONTACTO_WA  = "https://wa.me/59898925827";

  // === NUEVO: handler para detectar "Ninguna de las anteriores" y avisar + redirigir ===
  const handleCongregacionChange = async (e) => {
    const value = e.target.value;
    setCongregacion(value);

    if (value === "Ninguna de las anteriores") {
      await Swal.fire({
        icon: "info",
        title: "Solo para miembros",
        html: `
          <div style="text-align:left; line-height:1.5">
            <p>Esta interfaz web es solamente para aquellos miembros de algunas de las congregaciones en la lista.</p>
            <p>De todas formas, nos gustaría que te comuniques con el número de Whatsapp <b>${CONTACTO_TEL}</b> y te ayudaremos a poder avanzar. Gracias.</p>
            <p><a href="${CONTACTO_WA}" target="_blank" rel="noopener noreferrer">Abrir WhatsApp</a></p>
          </div>
        `,
        confirmButtonText: "Entendido",
      });

      // Limpieza y vuelta al Home (sin crear usuario)
      setNombre("");
      setApellido("");
      setEmail("");
      setPassword("");
      setCelular("");
      setCongregacion("");
      setModoRegistro(false);
      navigate("/");
    }
  };

  const enviarMailBienvenida = async (userName, userEmail) => {
    const subject = "Bienvenido/a · Reservas Cuchi";
    await enviarMail({
      to: userEmail,
      subject,
      html: `
        <div style="font-family:Arial,Helvetica,sans-serif; line-height:1.6; color:#222">
          <p>Hola ${userName},</p>
          <p>¡Tu cuenta fue creada con éxito! Ya podés reservar tu estadía.</p>

          <p style="margin:14px 0">
            <a href="${URL_RESERVAR}"
              style="display:inline-block; padding:10px 16px; background:#2e7d32; color:#fff; border-radius:6px; text-decoration:none;">
              Hacer una reserva
            </a>
          </p>

          <p>📎 Información útil (check-in, normas, pagos, etc.):
            <a href="${URL_INFO}" target="_blank" rel="noopener noreferrer">ver aquí</a>.
          </p>

          <p>📞 ¿Dudas o pagos? Escribinos por WhatsApp al
            <a href="${CONTACTO_WA}" target="_blank" rel="noopener noreferrer">${CONTACTO_TEL}</a>.
          </p>

          <hr style="border:none;border-top:1px solid #eee;margin:18px 0" />

          <p style="font-size:12px; color:#666">
            Si no ves este mensaje en tu bandeja de entrada, revisá la carpeta <b>No deseado/Spam</b> y marcá nuestro
            correo como <b>Seguro</b> (o <i>No es spam</i>) para recibir futuras confirmaciones correctamente.
          </p>
        </div>
      `,
      text:
        `Hola ${userName},\n` +
        `¡Tu cuenta fue creada con éxito! Ya podés reservar tu estadía.\n\n` +
        `Hacer una reserva: ${URL_RESERVAR}\n` +
        `Info útil: ${URL_INFO}\n` +
        `Contacto (WhatsApp): ${CONTACTO_TEL} - ${CONTACTO_WA}\n\n` +
        `Si no ves este correo, revisá Spam y marcá como Seguro/No es spam.`
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // === NUEVO: bloqueo explícito si eligió "Ninguna de las anteriores" ===
    if (congregacion === "Ninguna de las anteriores") {
      await Swal.fire({
        icon: "info",
        title: "Solo para miembros",
        html: `
          <div style="text-align:left; line-height:1.5">
            <p>Esta interfaz web es solamente para aquellos miembros de algunas de las congregaciones en la lista.</p>
            <p>De todas formas, nos gustaría que te comuniques con el número de Whatsapp <b>${CONTACTO_TEL}</b> y te ayudaremos a poder avanzar. Gracias.</p>
            <p><a href="${CONTACTO_WA}" target="_blank" rel="noopener noreferrer">Abrir WhatsApp</a></p>
          </div>
        `,
        confirmButtonText: "Entendido",
      });
      setModoRegistro(false);
      navigate("/");
      return; // 👈 MUY IMPORTANTE: no continuar con el registro
    }

    if (!nombre || !apellido || !email || !password || !celular || !congregacion) {
      Swal.fire("Error", "Por favor completa todos los campos.", "error");
      return;
    }

    try {
      const userCredential = await auth.createUserWithEmailAndPassword(email, password);
      const user = userCredential.user;
      await user.updateProfile({
        displayName: `${nombre} ${apellido}`,
      });

      const esAdmin = nombre.trim().toLowerCase() === "admin" && apellido.trim().toLowerCase() === "admin";
      const rol = esAdmin ? "admin" : "user";

      await firestore.collection("usuarios").doc(user.uid).set({
        nombre,
        apellido,
        email,
        celular,
        congregacion,
        rol,
      });

      await enviarMailBienvenida(`${nombre} ${apellido}`, email);

      const userDoc = await firestore.collection("usuarios").doc(user.uid).get();
      const userData = userDoc.exists ? userDoc.data() : {};

      setUsuario({
        uid: user.uid,
        email: user.email,
        nombre: userData.nombre || "",
        apellido: userData.apellido || "",
        celular: userData.celular || "",
        congregacion: userData.congregacion || "",
        rol: userData.rol || "user",
      });

      Swal.fire({
        icon: "success",
        title: "Cuenta creada 🎉",
        html: `
          <div style="text-align:left; line-height:1.5">
            <p>Te enviamos un correo de bienvenida.</p>
            <p style="margin-top:6px">
              <b>Importante:</b> revisá la carpeta <b>No deseado/Spam</b> y marcá nuestro correo como
              <b>Seguro</b> (o <i>No es spam</i>) para que los próximos mensajes lleguen a tu bandeja de entrada.
            </p>
            <p style="margin-top:10px">
              ¿Necesitás ayuda? WhatsApp:
              <a href="${CONTACTO_WA}" target="_blank" rel="noopener noreferrer">${CONTACTO_TEL}</a>.
            </p>
          </div>
        `,
        confirmButtonText: "Entendido",
      });

      navigate("/");
    } catch (error) {
      const mensaje = errorMessages[error.code] || "Error al registrarse. Inténtalo nuevamente.";
      Swal.fire("Error", mensaje, "error");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      // 1) Autenticación (si falla, mostramos el modal)
      const userCredential = await auth.signInWithEmailAndPassword(email, password);
      const user = userCredential.user;

      // 2) Lectura de Firestore (no crítica): envolver en try/catch SUAVE
      let userData = {};
      try {
        const userDoc = await firestore.collection("usuarios").doc(user.uid).get();
        userData = userDoc.exists ? userDoc.data() : {};
        if (!userDoc.exists) {
          console.warn("Usuario autenticado pero sin documento en Firestore");
        }
      } catch (err) {
        console.warn("Error al obtener datos de Firestore después de login:", err);
        // No mostramos modal aquí: el login ya fue exitoso
      }

      // 3) Continuar normalmente con lo que tengas
      setUsuario({
        uid: user.uid,
        email: user.email,
        nombre: userData.nombre || "",
        apellido: userData.apellido || "",
        celular: userData.celular || "",
        congregacion: userData.congregacion || "",
        rol: userData.rol || "user",
      });

      navigate("/");
    } catch (error) {
      // Falló la AUTENTICACIÓN → sí mostramos el modal
      const mensaje = errorMessages[error.code] || "Error al iniciar sesión. Inténtalo nuevamente.";
      Swal.fire("Error", mensaje, "error");
    }
  };

  const handlePasswordResetPrompt = async () => {
    const { value: correo } = await Swal.fire({
      title: "Restablecer contraseña",
      input: "email",
      inputLabel: "Ingresa tu correo electrónico",
      inputPlaceholder: "Correo electrónico",
      showCancelButton: true,
      confirmButtonText: "Enviar",
      cancelButtonText: "Cancelar",
    });
    if (correo) {
      try {
        await auth.sendPasswordResetEmail(correo);
        Swal.fire("Éxito", "Correo de restablecimiento enviado.", "success");
      } catch (error) {
        const mensaje =
          errorMessages[error.code] || "Error al enviar el correo. Inténtalo nuevamente.";
        Swal.fire("Error", mensaje, "error");
      }
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h2 className="login-title">{modoRegistro ? "Registrarse" : "Iniciar Sesión"}</h2>

        <form onSubmit={modoRegistro ? handleRegister : handleLogin}>
          {modoRegistro && (
            <>
              <label>
                Nombre:
                <input
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                />
              </label>

              <label>
                Apellido:
                <input
                  type="text"
                  value={apellido}
                  onChange={(e) => setApellido(e.target.value)}
                  required
                />
              </label>

              <label>
                Celular:
                <input
                  type="text"
                  value={celular}
                  onChange={(e) => setCelular(e.target.value)}
                  required
                />
              </label>

              <label>
                Congregación:
                <select
                  value={congregacion}
                  onChange={handleCongregacionChange}  // 👈 NUEVO
                  required
                >
                  <option value="">-- Seleccioná tu congregación --</option>
                  <option value="Igl. Mennonita de la Costa">Igl. Mennonita de la Costa</option>
                  <option value="Igl. Mennonita Delta">Igl. Mennonita Delta</option>
                  <option value="Igl. Mennonita El Ombú">Igl. Mennonita El Ombú</option>
                  <option value="Igl. Mennonita Gartental">Igl. Mennonita Gartental</option>
                  <option value="Ninguna de las anteriores">Ninguna de las anteriores</option>
                </select>
              </label>
            </>
          )}

          <label>
            Correo electrónico:
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>

          <label>
            Contraseña:
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>

          <div className="botones-contenedor">
            <button className="primary-btn" type="submit">
              {modoRegistro ? "Registrarse" : "Iniciar Sesión"}
            </button>

            <button
              type="button"
              className="secondary-btn"
              onClick={() => setModoRegistro(!modoRegistro)}
            >
              {modoRegistro ? "¿Ya tenés cuenta? Iniciá sesión" : "¿No tenés cuenta? Registrate"}
            </button>

            {!modoRegistro && (
              <button
                type="button"
                className="secondary-btn"
                onClick={handlePasswordResetPrompt}
              >
                Olvidé mi contraseña
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login;
